import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { keyframes, css } from 'styled-components';
import { FaChevronLeft, FaChevronRight, FaTimes, FaFistRaised, FaShieldAlt, FaBolt, FaCrosshairs, FaExchangeAlt, FaSkull, FaStar } from 'react-icons/fa';

// ─── Layout ────────────────────────────────────────────────────

/* ─── Shell chrome reskinned to mass-poster tokens ───
   Overlay/Slide/Nav now speak the same ink + amber language as the rest
   of the app. Slide CONTENT (diagrams, callout blocks further down this
   file) still uses its slate/indigo accent palette; that's scoped content,
   not chrome, so it can stay until a deeper content revision. */
const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(8, 7, 10, 0.92);
  backdrop-filter: blur(10px);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: var(--font-body);
  color: var(--color-cream-200);
`;

const SlideContainer = styled(motion.div)`
  width: 90%;
  max-width: 900px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 40px;
  background: linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
  box-shadow: var(--shadow-lg);
  position: relative;

  /* amber top rail */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--color-amber-500);
  }

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: var(--color-ink-900); }
  &::-webkit-scrollbar-thumb {
    background: var(--color-ink-600);
    &:hover { background: var(--color-amber-500); }
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: var(--color-cream-300);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out);
  z-index: 2;
  &:hover { color: var(--color-amber-500); }
`;

const Nav = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`;

const NavBtn = styled.button`
  background: ${p => p.disabled ? 'var(--color-ink-800)' : 'var(--color-amber-500)'};
  border: 1px solid ${p => p.disabled ? 'var(--color-ink-700)' : 'var(--color-amber-500)'};
  color: ${p => p.disabled ? 'var(--color-ink-500)' : 'var(--color-ink-950)'};
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${p => p.disabled ? 'default' : 'pointer'};
  clip-path: var(--clip-button);
  transition: filter var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);
  &:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
`;

const Dot = styled.div`
  width: ${p => p.$active ? '24px' : '8px'};
  height: 4px;
  background: ${p => p.$active ? 'var(--color-amber-500)' : 'var(--color-ink-600)'};
  transition: all 0.3s;
  cursor: pointer;
`;

const SlideTitle = styled.h2`
  font-family: var(--font-display);
  font-style: italic;
  font-size: 36px;
  font-weight: var(--weight-bold);
  letter-spacing: -0.01em;
  line-height: 1;
  margin: 0 0 6px;
  text-transform: uppercase;
  color: var(--color-amber-500);
`;

const SlideSubtitle = styled.p`
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-300);
  margin: 0 0 28px;
`;

const PageBadge = styled.span`
  display: block;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: var(--weight-semibold);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-cream-400);
  margin-bottom: 8px;
`;

// ─── Reusable visual pieces ────────────────────────────────────
// All primitives below speak mass-poster tokens: ink backgrounds, amber
// accents, cream text, red for danger. Overridable via $bg / $border /
// $color so individual slides can still tint their own semantics without
// reaching for hex codes.

const glow = keyframes`
  0%,100% { box-shadow: 0 0 8px rgba(255, 177, 60, 0.25); }
  50%     { box-shadow: 0 0 20px rgba(255, 177, 60, 0.55); }
`;

const Label = styled.div`
  font-family: var(--font-heading);
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: ${p => p.$color || 'var(--color-amber-500)'};
  margin-bottom: 6px;
`;

const Box = styled.div`
  background: ${p => p.$bg || 'var(--color-ink-900)'};
  border: 1px solid ${p => p.$border || 'var(--color-ink-700)'};
  clip-path: var(--clip-button);
  padding: ${p => p.$pad || '16px'};
  color: var(--color-cream-200);
  ${p => p.$glow && css`animation: ${glow} 2s infinite;`}
  transition: border-color var(--dur-fast) var(--ease-out);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: ${p => p.$gap || '12px'};
  ${p => p.$mt && `margin-top: ${p.$mt};`}
`;

const FlexRow = styled.div`
  display: flex;
  align-items: ${p => p.$align || 'center'};
  gap: ${p => p.$gap || '10px'};
  ${p => p.$mt && `margin-top: ${p.$mt};`}
  ${p => p.$wrap && 'flex-wrap: wrap;'}
`;

// ─── Mini Health Bar ──────────────────────────────────────────

const HealthBarOuter = styled.div`
  width: 100%;
  height: ${p => p.$h || '10px'};
  background: var(--color-ink-950);
  border: 1px solid var(--color-ink-700);
  overflow: hidden;
  position: relative;
`;

const HealthBarInner = styled(motion.div)`
  height: 100%;
  /* Cream → amber → red tail. Mass-poster doesn't use chroma green, so
     HP "safe" reads as cream with a slight amber warmth. */
  background: ${p =>
    p.$pct > 60 ? 'linear-gradient(90deg,var(--color-cream-200),var(--color-cream-100))' :
    p.$pct > 30 ? 'linear-gradient(90deg,var(--color-amber-600),var(--color-amber-500))' :
                  'linear-gradient(90deg,var(--color-red-600),var(--color-red-500))'};
`;

const MiniHealthBar = ({ pct, h }) => (
  <HealthBarOuter $h={h}>
    <HealthBarInner
      $pct={pct}
      initial={{ width: '100%' }}
      animate={{ width: `${pct}%` }}
      transition={{ duration: 1, ease: 'easeInOut' }}
    />
  </HealthBarOuter>
);

// ─── Mock Stat Row ────────────────────────────────────────────

const StatRowMock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: var(--color-ink-950);
  border: 1px solid var(--color-ink-700);
  border-left: 3px solid ${p => p.$color || 'var(--color-amber-500)'};
  clip-path: var(--clip-button);
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-cream-200);
`;

// ─── Mock Move Button ─────────────────────────────────────────
// Mirrors the real in-arena MoveTile: ink body, amber left rail, mono
// name — so the guide reads as a rehearsal of the real UI, not a
// separate visual language.

const MockMoveBtn = styled.div`
  position: relative;
  padding: 10px 14px;
  background: var(--color-ink-900);
  color: var(--color-cream-100);
  font-family: var(--font-heading);
  font-size: 12px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
  transition: border-color var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);

  /* Amber left rail, tint overridable per-move via $accent */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${p => p.$accent || 'var(--color-amber-500)'};
  }

  &:hover {
    border-color: var(--color-amber-500);
    transform: translateY(-1px);
  }
`;

const MockMoveBadge = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: var(--weight-semibold);
  letter-spacing: 0.04em;
  background: var(--color-ink-950);
  color: var(--color-cream-300);
  padding: 2px 8px;
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
`;

// ─── Flow Diagram pieces ──────────────────────────────────────

const flowAppear = keyframes`
  from { opacity:0; transform: translateY(10px); }
  to { opacity:1; transform: translateY(0); }
`;

const FlowStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  animation: ${flowAppear} 0.5s ease-out both;
  animation-delay: ${p => p.$delay || '0s'};
`;

/* Step marker — cut-corner square (not circle) to match the rest of the
   mass-poster kit. Amber by default, overridable via $bg for "decision"
   / "danger" moments in the turn flow. */
const FlowDot = styled.div`
  min-width: 32px;
  height: 32px;
  background: ${p => p.$bg || 'var(--color-amber-500)'};
  color: var(--color-ink-950);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-style: italic;
  font-size: 14px;
  font-weight: var(--weight-bold);
  clip-path: var(--clip-button);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 32px;
    left: 50%;
    transform: translateX(-50%);
    width: 1px;
    height: ${p => p.$last ? '0' : '28px'};
    background: var(--color-ink-600);
  }
`;

const FlowContent = styled.div`
  padding-bottom: ${p => p.$last ? '0' : '20px'};
`;

const FlowTitle = styled.div`
  font-family: var(--font-heading);
  font-size: 13px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-cream-100);
`;

const FlowDesc = styled.div`
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-cream-300);
  margin-top: 2px;
`;

// ─── Tooltip Popover ──────────────────────────────────────────

const TooltipBox = styled(motion.div)`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-ink-950);
  border: 1px solid var(--color-ink-700);
  border-top: 3px solid var(--color-amber-500);
  clip-path: var(--clip-button);
  padding: 14px;
  width: 240px;
  z-index: 5;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--color-cream-200);
  box-shadow: var(--shadow-lg);
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: var(--color-ink-950);
  }
`;

// ─── Annotated Region ─────────────────────────────────────────

const AnnotatedRegion = styled.div`
  border: 1px dashed ${p => p.$color || 'var(--color-ink-600)'};
  padding: 10px;
  position: relative;
  cursor: pointer;
  background: var(--color-ink-950);
  clip-path: var(--clip-button);
  transition: border-color var(--dur-fast) var(--ease-out),
              background var(--dur-fast) var(--ease-out);

  &:hover {
    border-color: ${p => p.$color || 'var(--color-amber-500)'};
    background: ${p => p.$hoverBg || 'rgba(255, 177, 60, 0.06)'};
  }
`;

const RegionLabel = styled.div`
  position: absolute;
  top: -10px;
  left: 12px;
  background: ${p => p.$bg || 'var(--color-amber-500)'};
  color: var(--color-ink-950);
  font-family: var(--font-heading);
  font-size: 9px;
  font-weight: var(--weight-bold);
  padding: 3px 8px;
  text-transform: uppercase;
  letter-spacing: var(--tracking-widest);
  clip-path: var(--clip-button);
`;

// ─── Comparison Card ──────────────────────────────────────────

const CompareCard = styled(Box)`
  text-align: center;
  border-color: ${p => p.$accent || 'var(--color-ink-700)'};
`;

const CompareTitle = styled.div`
  font-family: var(--font-heading);
  font-size: 14px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: ${p => p.$color || 'var(--color-amber-500)'};
  margin-bottom: 8px;
`;

// ═══════════════════════════════════════════════════════════════
// SLIDES
// ═══════════════════════════════════════════════════════════════

const Slide_Welcome = () => (
  <>
    <SlideTitle>How Battles Work</SlideTitle>
    <SlideSubtitle>Interactive guide to Movie Mayhem</SlideSubtitle>

    <Grid $cols="1fr 1fr 1fr" $gap="14px">
      {[
        { icon: <FaFistRaised size={20} />, title: 'Turn-Based', desc: 'Both players choose, then actions resolve by speed.' },
        { icon: <FaExchangeAlt size={20} />, title: 'Random roster', desc: 'Each match rolls your fighters and four moves each — switch when you need a new piece on the board.' },
        { icon: <FaStar size={20} />, title: 'Unique Moves', desc: 'Four moves per fighter — each with limited uses.' },
      ].map((item, i) => (
        <Box key={i} $pad="20px" style={{ textAlign: 'center' }}>
          <div style={{ color: 'var(--color-amber-500)', marginBottom: 10 }}>{item.icon}</div>
          <div style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--color-cream-100)',
            marginBottom: 6,
          }}>{item.title}</div>
          <div style={{
            fontSize: 12,
            lineHeight: 1.5,
            color: 'var(--color-cream-300)',
          }}>{item.desc}</div>
        </Box>
      ))}
    </Grid>

    <Box
      $pad="14px 18px"
      $bg="rgba(255, 177, 60, 0.08)"
      $border="var(--color-amber-500)"
      style={{ marginTop: 22, textAlign: 'center' }}
    >
      <span style={{ fontSize: 13, color: 'var(--color-cream-200)' }}>
        Press <strong style={{ color: 'var(--color-amber-500)' }}>→</strong> or click <strong style={{ color: 'var(--color-amber-500)' }}>Next</strong> to walk through the battle screen.
      </span>
    </Box>
  </>
);

// ─── Slide 2: Battle Screen Map ───────────────────────────────

const ScreenMapInfo = {
  team: { title: 'Your Team', desc: 'Four fighters on your bench this match (default 4v4). Click one to switch. Highlight = active; greyed = defeated.' },
  arena: { title: 'Battle Arena', desc: 'Your character (left) VS opponent (right). Shows image, stats, HP bar.' },
  moves: { title: 'Move Panel', desc: '4 move buttons. Colors show power level. Badge shows remaining uses.' },
  opponent: { title: 'Opponent Team', desc: 'Their four-fighter bench. Unrevealed slots show "?". Revealed when they enter battle.' },
  status: { title: 'Status & Timer', desc: 'Left: current game state. Right: countdown timer (runs out = you lose).' },
};

/* Color accents used in the screen-map mock. Amber = your side, red =
   opposition, cream = neutral UI chrome. */
const REGION_ACCENTS = {
  team:     { border: 'var(--color-amber-500)', hover: 'rgba(255, 177, 60, 0.08)', label: 'var(--color-amber-500)' },
  arena:    { border: 'var(--color-cream-200)',  hover: 'rgba(232, 223, 200, 0.06)', label: 'var(--color-cream-100)' },
  opponent: { border: 'var(--color-red-500)',    hover: 'rgba(193, 39, 45, 0.08)',   label: 'var(--color-red-500)' },
  moves:    { border: 'var(--color-amber-500)',  hover: 'rgba(255, 177, 60, 0.06)', label: 'var(--color-amber-500)' },
  status:   { border: 'var(--color-cream-300)',  hover: 'rgba(232, 223, 200, 0.05)', label: 'var(--color-cream-200)' },
};

const Slide_ScreenMap = () => {
  const [selected, setSelected] = useState(null);
  const info = selected ? ScreenMapInfo[selected] : null;

  return (
    <>
      <SlideTitle>Battle Screen Layout</SlideTitle>
      <SlideSubtitle>Tap any region to learn what it does</SlideSubtitle>

      <div style={{ display: 'grid', gridTemplateColumns: '88px 1fr 88px', gridTemplateRows: 'auto auto', gap: 8 }}>
        {/* Your Team — four bench slots (matches default 4v4 roster) */}
        <AnnotatedRegion
          $color={REGION_ACCENTS.team.border}
          $hoverBg={REGION_ACCENTS.team.hover}
          onClick={() => setSelected('team')}
          style={{ gridRow: '1 / 3', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, minHeight: 200 }}
        >
          <RegionLabel $bg={REGION_ACCENTS.team.label}>Your Team</RegionLabel>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{
              width: 46,
              height: 36,
              background: i === 1 ? 'rgba(255, 177, 60, 0.14)' : 'var(--color-ink-900)',
              border: i === 1 ? '2px solid var(--color-amber-500)' : '1px solid var(--color-ink-700)',
              clipPath: 'var(--clip-button)',
            }} />
          ))}
        </AnnotatedRegion>

        {/* Battle Arena */}
        <AnnotatedRegion
          $color={REGION_ACCENTS.arena.border}
          $hoverBg={REGION_ACCENTS.arena.hover}
          onClick={() => setSelected('arena')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 20, minHeight: 100 }}
        >
          <RegionLabel $bg={REGION_ACCENTS.arena.label}>Battle Arena</RegionLabel>
          <div style={{
            width: 60, height: 70,
            background: 'rgba(255, 177, 60, 0.14)',
            border: '1px solid var(--color-amber-500)',
            clipPath: 'var(--clip-button)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.14em', color: 'var(--color-amber-500)',
          }}>YOU</div>
          <div style={{
            fontFamily: 'var(--font-display)', fontStyle: 'italic',
            fontWeight: 800, color: 'var(--color-cream-100)', fontSize: 22,
          }}>VS</div>
          <div style={{
            width: 60, height: 70,
            background: 'rgba(193, 39, 45, 0.14)',
            border: '1px solid var(--color-red-500)',
            clipPath: 'var(--clip-button)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-heading)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.14em', color: 'var(--color-red-500)',
          }}>OPP</div>
        </AnnotatedRegion>

        {/* Opponent Team */}
        <AnnotatedRegion
          $color={REGION_ACCENTS.opponent.border}
          $hoverBg={REGION_ACCENTS.opponent.hover}
          onClick={() => setSelected('opponent')}
          style={{ gridRow: '1 / 3', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, minHeight: 200 }}
        >
          <RegionLabel $bg={REGION_ACCENTS.opponent.label}>Opponent</RegionLabel>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{
              width: 46,
              height: 36,
              background: i === 1 ? 'rgba(193, 39, 45, 0.14)' : 'var(--color-ink-900)',
              border: i === 1 ? '2px solid var(--color-red-500)' : '1px solid var(--color-ink-700)',
              clipPath: 'var(--clip-button)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontStyle: 'italic',
              fontWeight: 700, color: 'var(--color-cream-400)',
            }}>
              {i > 1 ? '?' : ''}
            </div>
          ))}
        </AnnotatedRegion>

        {/* Moves */}
        <AnnotatedRegion
          $color={REGION_ACCENTS.moves.border}
          $hoverBg={REGION_ACCENTS.moves.hover}
          onClick={() => setSelected('moves')}
          style={{ display: 'flex', gap: 6, justifyContent: 'center', padding: '12px 10px' }}
        >
          <RegionLabel $bg={REGION_ACCENTS.moves.label}>Moves</RegionLabel>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{
              flex: 1,
              height: 28,
              background: 'var(--color-ink-900)',
              border: '1px solid var(--color-ink-700)',
              borderLeft: '3px solid var(--color-amber-500)',
              clipPath: 'var(--clip-button)',
            }} />
          ))}
        </AnnotatedRegion>
      </div>

      {/* Status bar mock */}
      <AnnotatedRegion
        $color={REGION_ACCENTS.status.border}
        $hoverBg={REGION_ACCENTS.status.hover}
        onClick={() => setSelected('status')}
        style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', padding: '8px 16px' }}
      >
        <RegionLabel $bg={REGION_ACCENTS.status.label}>Status Bar</RegionLabel>
        <span style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--color-amber-500)',
        }}>Select a move</span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          color: 'var(--color-cream-200)',
        }}>5:00</span>
      </AnnotatedRegion>

      {/* Info panel */}
      <AnimatePresence mode="wait">
        {info && (
          <motion.div
            key={selected}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{ marginTop: 16 }}
          >
            <Box $bg="rgba(255, 177, 60, 0.06)" $border="var(--color-amber-500)" $pad="14px 16px">
              <div style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--color-amber-500)',
                marginBottom: 6,
              }}>{info.title}</div>
              <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>{info.desc}</div>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ─── Slide 3: Character Card Anatomy ──────────────────────────

/* Reusable chip for the "set HP" toggles in the anatomy slide. Inlined
   here because it's only used once — but factored out of the render so
   the JSX below stays readable. */
const HpChip = ({ value, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '5px 10px',
      fontFamily: 'var(--font-heading)',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      background: active ? 'var(--color-amber-500)' : 'var(--color-ink-900)',
      color: active ? 'var(--color-ink-950)' : 'var(--color-cream-200)',
      border: '1px solid ' + (active ? 'var(--color-amber-500)' : 'var(--color-ink-700)'),
      clipPath: 'var(--clip-button)',
      cursor: 'pointer',
    }}
  >
    {value}%
  </button>
);

const Slide_CardAnatomy = () => {
  const [demoHp, setDemoHp] = useState(100);

  return (
    <>
      <SlideTitle>Character Card</SlideTitle>
      <SlideSubtitle>Every element on an in-arena card, explained</SlideSubtitle>

      <Grid $cols="280px 1fr" $gap="24px">
        {/* Mock card — built to mirror the real mass-poster character card. */}
        <div style={{
          position: 'relative',
          background: 'linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%)',
          border: '1px solid var(--color-ink-700)',
          clipPath: 'var(--clip-card)',
          overflow: 'hidden',
        }}>
          {/* Amber top rail */}
          <div style={{ height: 3, background: 'var(--color-amber-500)' }} />
          {/* Portrait well */}
          <div style={{
            height: 100,
            background: 'radial-gradient(ellipse at 50% 30%, rgba(193, 39, 45, 0.22) 0%, transparent 70%), linear-gradient(180deg, var(--color-red-900) 0%, var(--color-ink-900) 80%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--color-cream-400)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
          }}>
            [ portrait ]
          </div>
          <div style={{ padding: '12px 14px 14px' }}>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontSize: 16,
              fontWeight: 700,
              textTransform: 'uppercase',
              textAlign: 'center',
              borderBottom: '1px solid var(--color-ink-700)',
              paddingBottom: 8,
              marginBottom: 10,
              color: 'var(--color-cream-100)',
            }}>
              Sivaji
            </div>

            <StatRowMock $color="var(--color-red-500)">
              <FaFistRaised size={10} color="var(--color-red-500)" />
              <span style={{ color: 'var(--color-cream-400)' }}>ATK</span>
              <span style={{ color: 'var(--color-cream-300)' }}>120</span>
              <span style={{ color: 'var(--color-ink-500)' }}>→</span>
              <span style={{ color: 'var(--color-amber-500)', fontWeight: 700 }}>180</span>
              <span style={{ color: 'var(--color-amber-400)', fontSize: 10 }}>1.5x</span>
            </StatRowMock>
            <StatRowMock $color="var(--color-cream-200)" style={{ marginTop: 4 }}>
              <FaShieldAlt size={10} color="var(--color-cream-200)" />
              <span style={{ color: 'var(--color-cream-400)' }}>DEF</span>
              <span style={{ fontWeight: 600 }}>100</span>
            </StatRowMock>
            <StatRowMock $color="var(--color-amber-500)" style={{ marginTop: 4 }}>
              <FaBolt size={10} color="var(--color-amber-500)" />
              <span style={{ color: 'var(--color-cream-400)' }}>SPD</span>
              <span style={{ fontWeight: 600 }}>85</span>
            </StatRowMock>

            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <span style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--color-cream-300)',
                letterSpacing: '0.04em',
              }}>
                HP {Math.round(450 * demoHp / 100)} / 450
              </span>
            </div>
            <div style={{ marginTop: 6 }}>
              <MiniHealthBar pct={demoHp} />
            </div>
          </div>
        </div>

        {/* Annotations */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Box $pad="10px 14px">
            <Label $color="var(--color-amber-500)">Top rail</Label>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>
              Amber strip signals the card is in its active (in-arena) state.
            </div>
          </Box>
          <Box $pad="10px 14px">
            <Label $color="var(--color-red-500)">ATK — Attack</Label>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>
              Damage dealt. When buffed the row shows{' '}
              <span style={{ color: 'var(--color-amber-500)' }}>base → boosted + multiplier</span>.
            </div>
          </Box>
          <Box $pad="10px 14px">
            <Label $color="var(--color-cream-200)">DEF — Defense</Label>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>
              Reduces incoming damage. Higher DEF = takes less.
            </div>
          </Box>
          <Box $pad="10px 14px">
            <Label $color="var(--color-amber-500)">SPD — Speed</Label>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>
              Determines who attacks first. The single most important stat.
            </div>
          </Box>
          <Box $pad="10px 14px">
            <Label $color="var(--color-amber-500)">HP bar</Label>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>
              <span style={{ color: 'var(--color-cream-100)' }}>Cream</span> &gt; 60% &nbsp;•&nbsp;
              <span style={{ color: 'var(--color-amber-500)' }}>Amber</span> 30–60% &nbsp;•&nbsp;
              <span style={{ color: 'var(--color-red-500)' }}>Red</span> &lt; 30%
            </div>
            <FlexRow $gap="8px" $mt="10px">
              {[100, 75, 45, 15].map(v => (
                <HpChip key={v} value={v} active={demoHp === v} onClick={() => setDemoHp(v)} />
              ))}
            </FlexRow>
          </Box>
        </div>
      </Grid>
    </>
  );
};

// ─── Slide 4: Moves & Actions ─────────────────────────────────

const Slide_Moves = () => {
  const [hovered, setHovered] = useState(null);

  /* accent colors map to move CATEGORY, not to a raw gradient; this is
     closer to how the real in-arena tiles read (ink body + one-color
     rail). */
  const moves = [
    { name: 'Justice Wrath', accent: 'var(--color-red-500)',    power: 120, acc: 85,  uses: '3/5', tip: 'Heavy damage, lower accuracy. Risk vs reward.' },
    { name: 'Multi Combat',  accent: 'var(--color-amber-500)',  power: 70,  acc: 95,  uses: '5/5', tip: 'Medium damage, reliable. Good for consistent pressure.' },
    { name: 'Oil Up',        accent: 'var(--color-cream-200)',  power: 0,   acc: 100, uses: '2/3', tip: 'Status move — boosts your ATK. No damage, huge payoff.' },
    { name: 'Guard Down',    accent: 'var(--color-red-400)',    power: 40,  acc: 100, uses: '4/4', tip: 'Low damage but lowers opponent DEF. Setup move.' },
  ];

  return (
    <>
      <SlideTitle>Move Selection</SlideTitle>
      <SlideSubtitle>Hover a move to see the tooltip</SlideSubtitle>

      <Grid $cols="1fr 1fr" $gap="10px">
        {moves.map((m, i) => (
          <div key={i} style={{ position: 'relative' }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            <MockMoveBtn $accent={m.accent}>
              <span>{m.name}</span>
              <MockMoveBadge>{m.uses}</MockMoveBadge>
            </MockMoveBtn>
            <AnimatePresence>
              {hovered === i && (
                <TooltipBox
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', marginBottom: 8,
                    fontFamily: 'var(--font-mono)', fontSize: 11,
                    color: 'var(--color-cream-200)',
                  }}>
                    <span><FaFistRaised size={10} /> PWR <strong style={{ color: 'var(--color-amber-500)' }}>{m.power || '—'}</strong></span>
                    <span><FaCrosshairs size={10} /> ACC <strong style={{ color: 'var(--color-amber-500)' }}>{m.acc}%</strong></span>
                  </div>
                  <div style={{ color: 'var(--color-cream-300)', fontSize: 12, lineHeight: 1.5 }}>{m.tip}</div>
                </TooltipBox>
              )}
            </AnimatePresence>
          </div>
        ))}
      </Grid>

      <div style={{ marginTop: 22 }}>
        <Label>Rail colour guide</Label>
        <Grid $cols="1fr 1fr" $gap="8px" $mt="8px">
          {[
            { color: 'var(--color-red-500)',    label: 'Red',    desc: 'High damage (power > 100)' },
            { color: 'var(--color-amber-500)',  label: 'Amber',  desc: 'Medium damage (50–100)' },
            { color: 'var(--color-cream-200)',  label: 'Cream',  desc: 'Buff / status move' },
            { color: 'var(--color-red-400)',    label: 'Rust',   desc: 'Utility / debuff move' },
            { color: 'var(--color-ink-500)',    label: 'Grey',   desc: 'Out of uses — disabled' },
          ].map((c, i) => (
            <FlexRow key={i} $gap="10px" style={{ fontSize: 12, color: 'var(--color-cream-200)' }}>
              <div style={{ width: 3, height: 22, background: c.color, flexShrink: 0 }} />
              <div>
                <strong style={{
                  fontFamily: 'var(--font-heading)',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  fontSize: 11,
                  color: 'var(--color-cream-100)',
                }}>{c.label}</strong>
                {' — '}{c.desc}
              </div>
            </FlexRow>
          ))}
        </Grid>
      </div>

      <Box $bg="rgba(255, 177, 60, 0.08)" $border="var(--color-amber-500)" $pad="12px 16px" style={{ marginTop: 16 }}>
        <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>
          <strong style={{ color: 'var(--color-amber-500)' }}>Uses badge</strong>{' '}
          (e.g. "3/5"): every move has limited uses per match. Uses are consumed even on a miss.
          When a move hits 0/X the tile greys out and can't be picked.
        </span>
      </Box>
    </>
  );
};

// ─── Slide 5: Turn Flow ───────────────────────────────────────

const Slide_TurnFlow = () => {
  /* Numbered steps use amber by default, red for the decision / danger
     moment ("did the defender faint?"), cream for the aftermath. Keeps
     the mass-poster palette while still visually ranking importance. */
  const steps = [
    { num: '1', bg: 'var(--color-amber-500)',  title: 'Both players choose an action', desc: 'Select a move or click a team member to switch. Then wait for the opponent.' },
    { num: '2', bg: 'var(--color-amber-500)',  title: 'Switches execute first',         desc: 'If either player switched, that happens before any attacks. The new character enters the field.' },
    { num: '3', bg: 'var(--color-amber-500)',  title: 'Compare speed stats',            desc: 'The character with higher SPD attacks first. If tied, random coin flip.' },
    { num: '4', bg: 'var(--color-amber-500)',  title: 'Faster character attacks',       desc: 'Move animation plays, damage is calculated, effects are applied.' },
    { num: '?', bg: 'var(--color-red-500)',    title: 'Did the defender faint?',        desc: 'If the defender\'s HP hits 0 they are knocked out. The slower character does NOT get to attack this turn.' },
    { num: '5', bg: 'var(--color-amber-500)',  title: 'Slower character attacks (if alive)', desc: 'If they survived, their move plays out. Then the turn is complete.' },
    { num: '6', bg: 'var(--color-cream-200)',  title: 'Fainted? Mandatory switch',      desc: 'The losing player must pick a replacement. This does NOT count as a turn.' },
  ];

  return (
    <>
      <SlideTitle>How a Turn Works</SlideTitle>
      <SlideSubtitle>The step-by-step flow of every turn in battle</SlideSubtitle>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {steps.map((s, i) => (
          <FlowStep key={i} $delay={`${i * 0.12}s`}>
            <FlowDot $bg={s.bg} $last={i === steps.length - 1}>{s.num}</FlowDot>
            <FlowContent $last={i === steps.length - 1}>
              <FlowTitle>{s.title}</FlowTitle>
              <FlowDesc>{s.desc}</FlowDesc>
            </FlowContent>
          </FlowStep>
        ))}
      </div>
    </>
  );
};

// ─── Slide 6: Damage Calculation ──────────────────────────────

const FormulaBox = styled.div`
  background: var(--color-ink-950);
  border: 1px solid var(--color-amber-500);
  clip-path: var(--clip-button);
  padding: 20px;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 15px;
  font-weight: var(--weight-semibold);
  letter-spacing: 0.04em;
  color: var(--color-amber-500);
  margin: 16px 0 20px;
`;

const Slide_Damage = () => {
  const [power, setPower] = useState(80);
  const [atk, setAtk] = useState(150);
  const [def, setDef] = useState(100);
  const damage = Math.ceil(power * (atk / def));

  const RangeInput = ({ label, value, onChange, min, max, color }) => (
    <div style={{ marginBottom: 14 }}>
      <FlexRow $gap="8px" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--color-cream-300)',
        }}>{label}</span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          color,
          fontWeight: 700,
        }}>{value}</span>
      </FlexRow>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: color }}
      />
    </div>
  );

  return (
    <>
      <SlideTitle>Damage Formula</SlideTitle>
      <SlideSubtitle>Drag the sliders to see damage change</SlideSubtitle>

      <FormulaBox>
        Damage = Power × ( ATK / DEF )
      </FormulaBox>

      <Grid $cols="1fr 1fr" $gap="20px">
        <div>
          <RangeInput label="Move Power"   value={power} onChange={setPower} min={20} max={200} color="var(--color-amber-500)" />
          <RangeInput label="Attacker ATK" value={atk}   onChange={setAtk}   min={50} max={300} color="var(--color-red-500)" />
          <RangeInput label="Defender DEF" value={def}   onChange={setDef}   min={50} max={300} color="var(--color-cream-200)" />
        </div>

        <Box $pad="20px" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--color-cream-400)',
            marginBottom: 6,
          }}>Total damage</div>
          <motion.div
            key={damage}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            style={{
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontSize: 52,
              fontWeight: 800,
              letterSpacing: '-0.01em',
              color: 'var(--color-amber-500)',
              lineHeight: 1,
            }}
          >
            {damage}
          </motion.div>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--color-cream-400)',
            marginTop: 10,
            textAlign: 'center',
          }}>
            {power} × ({atk} / {def}) = <strong style={{ color: 'var(--color-cream-100)' }}>{damage}</strong>
          </div>
          <div style={{ marginTop: 14, width: '100%' }}>
            <div style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--color-cream-400)',
              marginBottom: 6,
              textAlign: 'center',
            }}>If defender has 500 HP</div>
            <MiniHealthBar pct={Math.max(0, 100 - (damage / 500 * 100))} />
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--color-cream-300)',
              marginTop: 6,
              textAlign: 'center',
            }}>
              {Math.min(100, (damage / 500 * 100)).toFixed(1)}% of HP gone
            </div>
          </div>
        </Box>
      </Grid>

      <Box $bg="rgba(255, 177, 60, 0.08)" $border="var(--color-amber-500)" $pad="12px 16px" style={{ marginTop: 18 }}>
        <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>
          <strong style={{ color: 'var(--color-amber-500)' }}>Key insight:</strong>{' '}
          Boosting ATK by 1.5× multiplies ALL your damage by 1.5×. One buff turn can win the whole game.
        </span>
      </Box>
    </>
  );
};

// ─── Slide 7: Switching ───────────────────────────────────────

const Slide_Switching = () => {
  /* Tiny bullet row to keep the two compare-cards visually aligned. `tone`
     maps semantic pros/cons to the mass-poster palette (amber = good,
     red = bad, cream = neutral caveat). */
  const Bullet = ({ tone, children }) => {
    const color =
      tone === 'good' ? 'var(--color-amber-500)' :
      tone === 'bad'  ? 'var(--color-red-500)' :
      'var(--color-cream-300)';
    return (
      <FlexRow $gap="8px" style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>
        <span style={{ color, fontSize: 10, marginTop: 5 }}>●</span>
        <span>{children}</span>
      </FlexRow>
    );
  };

  return (
    <>
      <SlideTitle>Character Switching</SlideTitle>
      <SlideSubtitle>Two kinds of switch — knowing the difference is critical</SlideSubtitle>

      <Grid $cols="1fr 1fr" $gap="16px">
        <CompareCard $pad="20px" $accent="var(--color-amber-500)">
          <div style={{ color: 'var(--color-amber-500)', fontSize: 22, marginBottom: 10 }}><FaExchangeAlt /></div>
          <CompareTitle $color="var(--color-amber-500)">Voluntary Switch</CompareTitle>
          <div style={{ fontSize: 13, color: 'var(--color-cream-200)', textAlign: 'left', marginBottom: 10 }}>
            You click a teammate to swap in.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left' }}>
            <Bullet tone="bad">Uses your entire turn</Bullet>
            <Bullet tone="good">Happens BEFORE attacks</Bullet>
            <Bullet tone="bad">New character may take a hit</Bullet>
            <Bullet tone="neutral">Strategic — save a low-HP character</Bullet>
          </div>
        </CompareCard>

        <CompareCard $pad="20px" $accent="var(--color-red-500)">
          <div style={{ color: 'var(--color-red-500)', fontSize: 22, marginBottom: 10 }}><FaSkull /></div>
          <CompareTitle $color="var(--color-red-500)">Mandatory Switch</CompareTitle>
          <div style={{ fontSize: 13, color: 'var(--color-cream-200)', textAlign: 'left', marginBottom: 10 }}>
            Your active character was knocked out.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left' }}>
            <Bullet tone="good">Does NOT use a turn</Bullet>
            <Bullet tone="good">Free action — next turn is normal</Bullet>
            <Bullet tone="bad">You MUST choose a replacement</Bullet>
            <Bullet tone="neutral">Status bar says "Switch character"</Bullet>
          </div>
        </CompareCard>
      </Grid>

      <Box $bg="rgba(255, 177, 60, 0.08)" $border="var(--color-amber-500)" $pad="14px 16px" style={{ marginTop: 16 }}>
        <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-cream-200)' }}>
          <strong style={{ color: 'var(--color-amber-500)' }}>Speed + switch interaction:</strong>{' '}
          if you voluntarily switch and the opponent attacks, the switch resolves first — your NEW character takes the hit. Bring in a high-DEF character to tank.
        </span>
      </Box>
    </>
  );
};

// ─── Slide 8: Pro Tips ────────────────────────────────────────

const TipCard = styled(Box)`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const TipNum = styled.div`
  min-width: 32px;
  height: 32px;
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-style: italic;
  font-size: 16px;
  font-weight: var(--weight-bold);
  clip-path: var(--clip-button);
`;

const Slide_Tips = () => (
  <>
    <SlideTitle>Pro Tips</SlideTitle>
    <SlideSubtitle>Climb the ladder faster</SlideSubtitle>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {[
        { title: 'Lead with your fastest character', desc: 'A fast character can KO before the opponent moves. Winning the speed war is winning the game.' },
        { title: 'Buff before you attack',           desc: 'One ATK boost turn makes every subsequent attack 1.5× stronger. The math is in your favour.' },
        { title: "Don't waste nukes on low-HP targets", desc: 'If the opponent is at 5% HP, use a weak move. Save limited-use moves for fresh characters.' },
        { title: 'Switch to save, not to stall',     desc: 'Only voluntarily switch when it gives a clear advantage. Every switch costs a turn.' },
        { title: 'Track opponent move uses',         desc: 'If they used their 120-power move five times already, it\'s probably out of uses. They\'re stuck with weaker options.' },
        { title: 'Watch the timer',                  desc: 'Don\'t overthink. When in doubt, attack with your strongest available move.' },
      ].map((tip, i) => (
        <TipCard key={i} $pad="12px 16px">
          <TipNum>{i + 1}</TipNum>
          <div>
            <div style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-cream-100)',
              marginBottom: 4,
            }}>{tip.title}</div>
            <div style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--color-cream-300)',
            }}>{tip.desc}</div>
          </div>
        </TipCard>
      ))}
    </div>
  </>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const SLIDES = [
  Slide_Welcome,
  Slide_ScreenMap,
  Slide_CardAnatomy,
  Slide_Moves,
  Slide_TurnFlow,
  Slide_Damage,
  Slide_Switching,
  Slide_Tips,
];

const SLIDE_TITLES = [
  'Welcome',
  'Screen Layout',
  'Character Card',
  'Moves',
  'Turn Flow',
  'Damage',
  'Switching',
  'Tips',
];

const VisualBattleGuide = ({ isOpen, onClose }) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (isOpen) setPage(0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' && page < SLIDES.length - 1) setPage(p => p + 1);
      if (e.key === 'ArrowLeft' && page > 0) setPage(p => p - 1);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, page, onClose]);

  if (!isOpen) return null;

  const CurrentSlide = SLIDES[page];

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <AnimatePresence mode="wait">
        <SlideContainer
          key={page}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.25 }}
        >
          <CloseBtn onClick={onClose}><FaTimes size={14} /></CloseBtn>
          <PageBadge>{page + 1} / {SLIDES.length} — {SLIDE_TITLES[page]}</PageBadge>
          <CurrentSlide />
        </SlideContainer>
      </AnimatePresence>

      <Nav>
        <NavBtn disabled={page === 0} onClick={() => setPage(p => p - 1)}>
          <FaChevronLeft size={16} />
        </NavBtn>
        <Dots>
          {SLIDES.map((_, i) => (
            <Dot key={i} $active={i === page} onClick={() => setPage(i)} />
          ))}
        </Dots>
        <NavBtn disabled={page === SLIDES.length - 1} onClick={() => setPage(p => p + 1)}>
          <FaChevronRight size={16} />
        </NavBtn>
      </Nav>
    </Overlay>
  );
};

export default VisualBattleGuide;
